import type { Routes } from '@angular/router';
import { OnyxFormMode } from '@onyx/angular';
import { permissionGuard } from '../../../auth/common/guards/permission.guard';
import type { FormResolverData } from '../../../common/interfaces/common/form-resolver-data';
import type { CompanyData } from '../../../common/interfaces/data-providers/company-data';
import { importGuard } from '../../common/guards/import.guard';
import { CONTRACTORS_IMPORT_COLUMNS } from './common/constants/contractors-import-columns';
import { addContractorGuard } from './common/guards/add-contractor.guard';
import type { ContractorForm } from './common/helpers/contractor-form.helper';
import { addContractorResolver } from './common/resolvers/add-contractor.resolver';
import { contractorCardResolver } from './common/resolvers/contractor-card.resolver';
import { editContractorResolver } from './common/resolvers/edit-contractor.resolver';
import { importContractorsResolver } from './common/resolvers/import-contractors.resolver';

export type ContractorFormResolverData = FormResolverData<ContractorForm> & {
  companyData: CompanyData | null;
};

export const CONTRACTORS_ROUTES: Routes = [
  {
    path: '',
    pathMatch: 'prefix',
    loadComponent: () =>
      import('./contractors-list/contractors-list.component').then(
        (m) => m.ContractorsListComponent,
      ),
    canActivate: [permissionGuard('contractors.get')],
  },
  {
    path: 'add',
    loadComponent: () =>
      import('./contractor-form/contractor-form.component').then(
        (m) => m.ContractorFormComponent,
      ),
    canActivate: [permissionGuard('contractors.add'), addContractorGuard],
    data: { mode: OnyxFormMode.ADD },
    resolve: { data: addContractorResolver },
  },
  {
    path: 'import',
    loadComponent: () =>
      import('../../common/components/import/import-result/import-result.component').then(
        (m) => m.ImportResultComponent,
      ),
    canActivate: [permissionGuard('contractors.add'), importGuard],
    data: { columns: CONTRACTORS_IMPORT_COLUMNS },
    resolve: { import: importContractorsResolver },
  },
  {
    path: ':uuid',
    children: [],
    canActivate: [permissionGuard('contractors.get')],
    resolve: { _: contractorCardResolver },
  },
  {
    path: ':uuid/edit',
    loadComponent: () =>
      import('./contractor-form/contractor-form.component').then(
        (m) => m.ContractorFormComponent,
      ),
    canActivate: [permissionGuard('contractors.edit')],
    data: { mode: OnyxFormMode.EDIT },
    resolve: { data: editContractorResolver },
  },
] as const;
